import type { ComputedRef, Ref } from 'vue';
import { transform } from 'lodash-es';
import { useRouter } from 'vue-router';

import type {
  IAffiliateProgramCommissionAttributePayload,
  IAffiliateProgramCommissionFilterFormWithErrors,
  IAffiliateProgramCommissionFilterPayload,
  IAffiliateProgramCommissionFormSchema,
  IAffiliateProgramCommissionItem,
  IAffiliateProgramCommissionPayload,
  IConditionOptions,
  IConditionVersions,
  ISelectedVersions,
} from '@modules/affiliate-program-commissions/submodules/edit/types';
import type { IApiFormErrors } from '@modules/core/services/query/types';
import { AFFILIATE_PROGRAM_COMMISSIONS_LIST_ROUTE_NAME } from '@modules/affiliate-program-commissions/constants';
import { AFFILIATE_PROGRAM_COMMISSIONS_ATTRIBUTES } from '@modules/affiliate-program-commissions/submodules/edit/constants';
import { useAffiliateProgramCommissionsSaveMutation } from '@modules/affiliate-program-commissions/submodules/edit/mutations';
import { EAasmState } from '@modules/core/enums';

export interface SaveCommissionComposable {
  onSubmit(values: IAffiliateProgramCommissionFormSchema): Promise<void>;
  isLoadingSave: Ref<boolean>;
  errors: Ref<IApiFormErrors | undefined>;
  clearError(key: string): void;
}

export default function useSaveCommission(
  commission: Ref<IAffiliateProgramCommissionItem | undefined>,
  countryFiltersWithErrors: Ref<IAffiliateProgramCommissionFilterFormWithErrors[]>,
  totalFilterErrors: Ref<IAffiliateProgramCommissionFilterFormWithErrors[] | undefined>,
  selectedVersions: ComputedRef<ISelectedVersions>,
  selectedProduct: Ref<string | undefined>,
  currentSecondGroupVersion: ComputedRef<IConditionVersions | undefined>,
): SaveCommissionComposable {
  const router = useRouter();
  const {
    mutateAsync,
    isLoading: isLoadingSave,
    errors,
    clearError,
  } = useAffiliateProgramCommissionsSaveMutation();

  function getCommissionAttributes(values: IAffiliateProgramCommissionFormSchema): IAffiliateProgramCommissionAttributePayload[] {
    const commissions_attributes: IAffiliateProgramCommissionAttributePayload[] = [
      ...(commission.value?.commissions_attributes ?? []),
    ];

    if (values.type_of_transaction === 'revenue_share' || values.type_of_transaction === 'hybrid') {
      for (const commission_type of AFFILIATE_PROGRAM_COMMISSIONS_ATTRIBUTES) {
        const attributeIndex = commissions_attributes.findIndex((item) => (
          item.commission_type === commission_type
        ));

        if (attributeIndex > -1) {
          commissions_attributes.splice(attributeIndex, 1, {
            ...(commissions_attributes[attributeIndex] || {}),
            commission_type,
            value: values[commission_type] || 0,
          });
        } else {
          commissions_attributes.push({
            commission_type,
            value: values[commission_type] || 0,
          });
        }
      }
    }

    return commissions_attributes;
  }

  function buildConditionOptions(): IConditionOptions {
    const result = transform<ISelectedVersions, IConditionOptions>(selectedVersions.value, (acc, version, key) => {
      const value: IConditionOptions = {
        [key]: { version },
      };

      if (currentSecondGroupVersion.value === 'v2' && value.revenue_share) {
        value.revenue_share.ngr_formula_modifier = selectedProduct.value;
      }

      Object.assign(acc, value);
      return acc;
    }, {});

    return result;
  }

  async function onSubmit(values: IAffiliateProgramCommissionFormSchema): Promise<void> {
    if (totalFilterErrors.value?.length) {
      return;
    }

    const payload: IAffiliateProgramCommissionPayload = {
      ...(commission.value ?? {}),
      options: buildConditionOptions(),
      accrual_of_interest_on_the_upper_interval: values.accrual_of_interest_on_the_upper_interval,
      transferring_a_negative_commission: values.transferring_a_negative_commission,
      merchant_id: values.merchant_id,
      type_of_transaction: values.type_of_transaction,
      title: values.title,
      description: values.description,
      sum_by_merchant_all: values.sum_by_merchant_all ?? false,
      commissions_attributes: getCommissionAttributes(values),
      condition_filters_attributes: countryFiltersWithErrors.value.map(({
        isAllCountries,
        label,
        emptyLabel,
        emptyLabelId,
        expanded,
        errors: conditionErrors,
        ...item
      }) => ({
        ...item,
        type: 'ConditionFilterCountry',
        name: isAllCountries ? 'filters.filter_all_countries' : label,
        value: item.value,
        condition_items_attributes: item.condition_items_attributes,
        condition_steps_attributes: item.condition_steps_attributes,
        aasm_state: isAllCountries ? EAasmState.DEFAULT : EAasmState.CUSTOM,
      } as IAffiliateProgramCommissionFilterPayload)),
    };

    try {
      await mutateAsync(payload);
      router.back({
        name: AFFILIATE_PROGRAM_COMMISSIONS_LIST_ROUTE_NAME,
      });
    } catch {}
  }

  return {
    onSubmit,
    isLoadingSave,
    errors,
    clearError,
  };
}
